import { useState, useEffect, useRef, useMemo, useLayoutEffect } from "react";
import { useOutletContext } from "react-router-dom";
import {
  FileText, Plus, Printer, Trash2, Loader2, Eye, Upload, Sparkles,
  Layers, Pencil, X, FileStack, ArrowLeft, Indent, Pilcrow, CornerDownLeft, Table, ListOrdered,
} from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/pages/Residents";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";

const GREEN = "#064E3B";

// ---- Autofill dari Data Warga: cocokkan key field template (fleksibel/alias) ----
function normKey(k) {
  return String(k || "").toLowerCase().trim().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
}
const RESIDENT_ALIASES = {
  nama: (r) => r.full_name, nama_lengkap: (r) => r.full_name, nama_pemohon: (r) => r.full_name, nama_warga: (r) => r.full_name,
  nik: (r) => r.nik, no_nik: (r) => r.nik, nomor_nik: (r) => r.nik,
  no_kk: (r) => r.kk_number, nomor_kk: (r) => r.kk_number, kk: (r) => r.kk_number, kk_number: (r) => r.kk_number,
  nomor_kartu_keluarga: (r) => r.kk_number, no_kartu_keluarga: (r) => r.kk_number,
  tempat_lahir: (r) => r.birth_place,
  tanggal_lahir: (r) => r.birth_date, tgl_lahir: (r) => r.birth_date,
  tempat_tanggal_lahir: (r) => [r.birth_place, r.birth_date].filter(Boolean).join(", "),
  ttl: (r) => [r.birth_place, r.birth_date].filter(Boolean).join(", "),
  jenis_kelamin: (r) => r.gender, kelamin: (r) => r.gender,
  agama: (r) => r.religion,
  pekerjaan: (r) => r.occupation, profesi: (r) => r.occupation,
  alamat: (r) => r.address, alamat_lengkap: (r) => r.address, alamat_pemohon: (r) => r.address, alamat_ktp: (r) => r.address,
  kewarganegaraan: (r) => r.citizenship, warga_negara: (r) => r.citizenship,
  status_perkawinan: (r) => r.marital_status, status_kawin: (r) => r.marital_status,
  status_nikah: (r) => r.marital_status, status_pernikahan: (r) => r.marital_status,
  pendidikan: (r) => r.education, pendidikan_terakhir: (r) => r.education,
  golongan_darah: (r) => r.blood_type, gol_darah: (r) => r.blood_type,
  rt: (r) => r.rt, rw: (r) => r.rw, dusun: (r) => r.dusun,
  nama_ayah: (r) => r.father_name, ayah: (r) => r.father_name,
  nama_ibu: (r) => r.mother_name, ibu: (r) => r.mother_name,
};
function residentValueFor(key, r, ctx) {
  const nk = normKey(key);
  const p = (ctx && ctx.profile) || {};
  // Alamat = alamat warga SAJA (jangan digabung wilayah). Desa/Kecamatan/Kabupaten diisi terpisah di field masing-masing.
  if (["alamat", "alamat_lengkap", "alamat_pemohon", "alamat_ktp", "domisili", "alamat_domisili", "tempat_tinggal"].includes(nk)) {
    return r.address || "";
  }
  // Komponen wilayah (ambil dari profil desa — semua warga berada di desa ini)
  if (["desa", "nagari", "gampong", "kampung", "kelurahan", "nama_desa", "desa_kelurahan", "kalurahan", "pekon", "kelurahan_desa"].includes(nk)) return p.village_name || "";
  if (["kecamatan", "nama_kecamatan", "distrik", "kapanewon"].includes(nk)) return p.district_name || "";
  if (["kabupaten", "kota", "kabupaten_kota", "kabkota", "nama_kabupaten"].includes(nk)) return p.regency_name || "";
  if (["provinsi", "nama_provinsi"].includes(nk)) return p.province_name || "";
  if (["kode_pos", "kodepos"].includes(nk)) return r.postal_code || p.postal_code || "";
  const fn = RESIDENT_ALIASES[nk];
  const v = fn ? fn(r) : "";
  return v == null ? "" : v;
}
function buildAutofill(template, r, ctx) {
  const patch = {};
  (template?.fields || []).forEach((f) => {
    const v = residentValueFor(f.key, r, ctx);
    if (v) patch[f.key] = v;
  });
  return patch;
}

// ---- TABEL DINAMIS: parse blok [[TABLE]] jadi spesifikasi kolom + jumlah baris awal ----
const TABLE_BLOCK_RE = /\[\[TABLE\]\]\n([\s\S]*?)\n\[\[\/TABLE\]\]/g;
function stripRowSuffix(base) { return String(base || "").replace(/_\d+$/, ""); }
function splitCells(line) { return (line || "").trim().replace(/^\||\|$/g, "").split("|").map((c) => c.trim()); }

function parseTableSpecs(body) {
  const specs = [];
  const re = new RegExp(TABLE_BLOCK_RE.source, "g");
  let m;
  while ((m = re.exec(body || "")) !== null) {
    const lines = m[1].split("\n").map((l) => l.trim()).filter(Boolean);
    if (!lines.length) continue;
    const header = splitCells(lines[0]);
    const dataLines = lines.slice(1).filter((l) => !/^[\s|:-]*$/.test(l.replace(/\|/g, "")));
    const first = splitCells(dataLines[0] || "");
    const cols = header.map((h, ci) => {
      const cell = first[ci] ?? "";
      const tok = cell.match(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/);
      if (tok) return { label: h, kind: "input", base: stripRowSuffix(tok[1]) };
      if (/^\d+$/.test(cell)) return { label: h, kind: "auto" };
      return { label: h, kind: "static", value: cell };
    });
    specs.push({ block: m[0], header, cols, rowCount: Math.max(1, dataLines.length) });
  }
  return specs;
}

// Bangun isi surat efektif: blok [[TABLE]] diganti dgn baris sesuai data pengguna (dinamis).
function buildEffectiveBody(body, specs, tableData) {
  let out = body || "";
  specs.forEach((spec, ti) => {
    const rows = tableData[ti] || [];
    const lines = [spec.header.join(" | ")];
    rows.forEach((row, ri) => {
      const cells = spec.cols.map((c, ci) => {
        if (c.kind === "auto") return String(ri + 1);
        if (c.kind === "static") return c.value;
        return String(row[ci] ?? "").trim();
      });
      lines.push(cells.join(" | "));
    });
    out = out.replace(spec.block, `[[TABLE]]\n${lines.join("\n")}\n[[/TABLE]]`);
  });
  return out;
}

// Kolom tabel yang bisa diisi otomatis dari Data Warga?
const RES_WILAYAH = ["desa", "nagari", "gampong", "kampung", "kelurahan", "nama_desa", "kecamatan", "nama_kecamatan",
  "kabupaten", "kota", "nama_kabupaten", "provinsi", "nama_provinsi", "alamat", "alamat_lengkap", "domisili", "tempat_tinggal", "kode_pos"];
function isResidentBase(base) {
  const nk = normKey(base);
  return !!RESIDENT_ALIASES[nk] || RES_WILAYAH.includes(nk);
}
// Semua token {{key}} yang berada di dalam blok [[TABLE]] (agar tidak ditampilkan sbg field biasa).
function tableTokenSet(body) {
  const set = new Set();
  const re = new RegExp(TABLE_BLOCK_RE.source, "g");
  let m;
  while ((m = re.exec(body || "")) !== null) {
    const tre = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g;
    let t;
    while ((t = tre.exec(m[1])) !== null) set.add(t[1]);
  }
  return set;
}

// Deteksi apakah template memuat field alamat (domisili warga)
const ADDR_KEYS = ["alamat", "alamat_lengkap", "alamat_pemohon", "alamat_ktp", "domisili", "alamat_domisili", "tempat_tinggal"];
function templateHasAddress(tmpl) {
  return (tmpl?.fields || []).some((f) => ADDR_KEYS.includes(normKey(f.key)));
}
// "Keperluan/tujuan/maksud" selalu OPSIONAL untuk surat apa pun.
function isOptionalPurposeKey(key) {
  const k = String(key || "").toLowerCase().replace(/[^a-z]/g, "");
  return k.includes("keperluan") || k === "tujuan" || k === "maksud" || k.includes("maksuddantujuan") || k.includes("untukkeperluan");
}
function isRequiredField(f) {
  return !!(f && f.required) && !isOptionalPurposeKey(f.key);
}

// Nilai wilayah desa (sama untuk semua warga — diambil dari profil desa)
function wilayahDefaults(profile) {
  const p = profile || {};
  return {
    nama_desa: p.village_name || "",
    nama_kecamatan: p.district_name || "",
    nama_kabupaten: p.regency_name || "",
    nama_provinsi: p.province_name || "",
  };
}

// Konversi tanggal DD-MM-YYYY <-> ISO YYYY-MM-DD untuk input type=date
function dateToISO(s) {
  if (!s) return "";
  let m = String(s).match(/^(\d{2})[-/](\d{2})[-/](\d{4})$/);
  if (m) return `${m[3]}-${m[2]}-${m[1]}`;
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  return "";
}
function dateFromISO(iso) {
  const m = String(iso || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}-${m[2]}-${m[1]}` : (iso || "");
}
function DateField({ value, onChange, testid, placeholder, disabled }) {
  const iso = dateToISO(value);
  // Jika nilai tersimpan tak bisa di-parse jadi tanggal, tampilkan sebagai teks biasa
  if (value && !iso) {
    return <Input value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled} className="h-11" data-testid={testid} placeholder={placeholder} />;
  }
  return <Input type="date" value={iso} onChange={(e) => onChange(dateFromISO(e.target.value))} disabled={disabled} className="h-11" data-testid={testid} />;
}


export default function Letters() {
  const { profile } = useOutletContext();
  const [tab, setTab] = useState("letters");
  const [templates, setTemplates] = useState([]);
  const [items, setItems] = useState([]);
  const [residents, setResidents] = useState([]);
  const [terms, setTerms] = useState(null);
  const [loading, setLoading] = useState(true);

  const [workspace, setWorkspace] = useState(null); // null | {mode:'create'|'view', letter?}

  const load = async () => {
    setLoading(true);
    try {
      const [tpl, l, r, t] = await Promise.all([
        api.get("/letters/templates"), api.get("/letters"),
        api.get("/residents"), api.get("/region-terms"),
      ]);
      setTemplates(tpl.data); setItems(l.data); setResidents(r.data); setTerms(t.data.effective);
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    try { await api.delete(`/letters/${id}`); toast.success("Surat dihapus"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  // Mode split-screen (buat / lihat surat) — menggantikan daftar, bukan popup.
  if (workspace) {
    return (
      <LetterWorkspace
        mode={workspace.mode} letter={workspace.letter}
        templates={templates} residents={residents} profile={profile} terms={terms}
        onBack={() => setWorkspace(null)}
        onCreated={(letter) => { load(); setWorkspace({ mode: "view", letter }); }}
      />
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <PageHeader
        icon={FileText} title="Surat Menyurat" subtitle="Terbitkan surat resmi & kelola template surat desa"
        action={
          <Button onClick={() => setWorkspace({ mode: "create" })} data-testid="letter-add-button" className="bg-[#064E3B] hover:bg-[#043E2F] rounded-xl h-11">
            <Plus size={18} className="mr-1.5" /> Buat Surat
          </Button>
        }
      />

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 p-1 rounded-xl w-fit">
        <button onClick={() => setTab("letters")} data-testid="tab-letters"
          className={`px-4 py-2 rounded-lg text-sm font-medium transition ${tab === "letters" ? "bg-white shadow text-[#064E3B]" : "text-slate-500"}`}>
          <FileStack size={16} className="inline mr-1.5 -mt-0.5" />Surat Terbit
        </button>
        <button onClick={() => setTab("templates")} data-testid="tab-templates"
          className={`px-4 py-2 rounded-lg text-sm font-medium transition ${tab === "templates" ? "bg-white shadow text-[#064E3B]" : "text-slate-500"}`}>
          <Layers size={16} className="inline mr-1.5 -mt-0.5" />Template Surat
        </button>
      </div>

      {tab === "letters" ? (
        <LettersList loading={loading} items={items} onPreview={(l) => setWorkspace({ mode: "view", letter: l })} onRemove={remove} />
      ) : (
        <TemplatesPanel templates={templates} onChanged={load} profile={profile} terms={terms} />
      )}
    </div>
  );
}

/* ---------------- Split-screen: form + pratinjau langsung ---------------- */
function LetterWorkspace({ mode, letter, templates, residents, profile, terms, onBack, onCreated }) {
  const isView = mode === "view";
  const [code, setCode] = useState(letter?.template_code || "");
  const [values, setValues] = useState(letter?.field_values || {});
  const [residentNik, setResidentNik] = useState(letter?.resident_nik || "");
  const [nomor, setNomor] = useState("");
  const [showOpt, setShowOpt] = useState({});
  const [signerMode, setSignerMode] = useState({}); // per name_key: "manual" | "warga"
  const [saving, setSaving] = useState(false);

  // Kontrol tampilan
  const [paper, setPaper] = useState("A4");
  const [showKopAddress, setShowKopAddress] = useState(true);
  const [showKop, setShowKop] = useState(true);
  const [showNumber, setShowNumber] = useState(true);
  const [lineSpacing, setLineSpacing] = useState(1.5);
  const [fontPt, setFontPt] = useState(11);

  // Data pratinjau (dari backend, tanpa disimpan)
  const [previewDoc, setPreviewDoc] = useState(isView ? letter : null);
  const [previewing, setPreviewing] = useState(false);

  const template = useMemo(() => templates.find((t) => t.code === code), [templates, code]);
  const ctx = useMemo(() => ({ profile, terms }), [profile, terms]);

  // KOP mengikuti setelan template (has_kop); bisa ditimpa manual lewat toggle.
  useEffect(() => { if (template) setShowKop(template.has_kop !== false); }, [template]);
  useEffect(() => { if (template) setShowNumber(template.has_number !== false); }, [template]);

  // ---- Tabel dinamis ----
  const tableSpecs = useMemo(() => parseTableSpecs(template?.body_template || ""), [template]);
  const tableTokenKeys = useMemo(() => tableTokenSet(template?.body_template || ""), [template]);
  const [tableData, setTableData] = useState({});
  useEffect(() => {
    const init = {};
    tableSpecs.forEach((spec, ti) => { init[ti] = Array.from({ length: spec.rowCount }, () => ({})); });
    setTableData(init);
  }, [tableSpecs]);
  const effectiveBody = useMemo(
    () => (tableSpecs.length ? buildEffectiveBody(template?.body_template || "", tableSpecs, tableData) : null),
    [template, tableSpecs, tableData],
  );
  const addRow = (ti) => setTableData((d) => ({ ...d, [ti]: [...(d[ti] || []), {}] }));
  const removeRow = (ti, ri) => setTableData((d) => ({ ...d, [ti]: (d[ti] || []).filter((_, i) => i !== ri) }));
  const setCell = (ti, ri, ci, val) => setTableData((d) => {
    const rows = [...(d[ti] || [])];
    rows[ri] = { ...(rows[ri] || {}), [ci]: val };
    return { ...d, [ti]: rows };
  });
  const fillRowFromResident = (ti, ri, nik) => {
    const r = residents.find((x) => x.nik === nik);
    if (!r) return;
    const spec = tableSpecs[ti];
    setTableData((d) => {
      const rows = [...(d[ti] || [])];
      const row = { ...(rows[ri] || {}) };
      spec.cols.forEach((c, ci) => {
        if (c.kind === "input" && isResidentBase(c.base)) {
          const v = residentValueFor(c.base, r, ctx);
          if (v) row[ci] = v;
        }
      });
      rows[ri] = row;
      return { ...d, [ti]: rows };
    });
  };

  const pickResident = (nik) => {
    setResidentNik(nik);
    const r = residents.find((x) => x.nik === nik);
    if (!r || !template) return;
    setValues((v) => {
      const nv = { ...v, ...buildAutofill(template, r, ctx) };
      if (templateHasAddress(template)) Object.assign(nv, wilayahDefaults(profile));
      return nv;
    });
  };

  const chooseTemplate = (v) => {
    setCode(v);
    const tmpl = templates.find((t) => t.code === v);
    const r = residentNik ? residents.find((x) => x.nik === residentNik) : null;
    const nv = r && tmpl ? buildAutofill(tmpl, r, ctx) : {};
    if (tmpl && templateHasAddress(tmpl)) Object.assign(nv, wilayahDefaults(profile));
    setValues(nv);
  };

  // Pratinjau langsung: panggil backend (debounce) tiap kali isian berubah (mode create).
  useEffect(() => {
    if (isView || !code) { if (!isView) setPreviewDoc(null); return; }
    setPreviewing(true);
    const h = setTimeout(async () => {
      try {
        const r = await api.post("/letters/preview", {
          template_code: code, resident_nik: residentNik, field_values: values,
          nomor_urut: nomor.trim() || undefined,
          body_override: effectiveBody || undefined,
        });
        setPreviewDoc(r.data);
      } catch (_) { /* diamkan error pratinjau */ } finally { setPreviewing(false); }
    }, 400);
    return () => clearTimeout(h);
  }, [code, values, nomor, residentNik, isView, effectiveBody]);

  const submit = async () => {
    if (!template) { toast.error("Pilih jenis surat"); return; }
    for (const f of template.fields || []) {
      if (tableTokenKeys.has(f.key)) continue; // token tabel diisi lewat editor tabel
      if (isRequiredField(f) && !String(values[f.key] || "").trim()) {
        toast.error(`Isi "${f.label}"`); return;
      }
    }
    setSaving(true);
    try {
      const r = await api.post("/letters", { template_code: code, resident_nik: residentNik, field_values: values, nomor_urut: nomor.trim() || undefined, body_override: effectiveBody || undefined });
      toast.success(`Surat ${r.data.letter_number} diterbitkan`);
      onCreated(r.data);
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  // Cetak: portal dokumen ke <body> agar hasil cetak tidak kosong.
  const handlePrint = () => {
    const node = document.querySelector(".letter-doc");
    if (!node) { window.print(); return; }
    const holder = document.createElement("div");
    holder.className = "letter-print-root";
    holder.innerHTML = node.outerHTML;
    document.body.appendChild(holder);
    document.body.classList.add("letter-printing");
    const cleanup = () => {
      document.body.classList.remove("letter-printing");
      holder.remove();
      window.removeEventListener("afterprint", cleanup);
    };
    window.addEventListener("afterprint", cleanup);
    window.print();
    setTimeout(cleanup, 1500);
  };

  const renderInput = (f) => (
    f.type === "textarea" ? (
      <Textarea value={values[f.key] || ""} onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
        rows={3} disabled={isView} data-testid={`field-${f.key}`} placeholder={f.placeholder} />
    ) : f.type === "date" ? (
      <DateField value={values[f.key] || ""} onChange={(val) => setValues({ ...values, [f.key]: val })}
        disabled={isView} testid={`field-${f.key}`} placeholder={f.placeholder} />
    ) : (
      <Input type={f.type === "number" ? "number" : "text"}
        value={values[f.key] || ""} onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
        className="h-11" disabled={isView} data-testid={`field-${f.key}`} placeholder={f.placeholder} />
    )
  );

  return (
    <div className="max-w-[1500px] mx-auto space-y-4">
      <div className="flex items-center gap-3">
        <Button variant="outline" onClick={onBack} className="rounded-xl h-10" data-testid="letter-back-button">
          <ArrowLeft size={18} className="mr-1.5" /> Kembali
        </Button>
        <h2 className="font-heading text-xl text-slate-900">{isView ? "Pratinjau Surat" : "Buat Surat Baru"}</h2>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
        {/* PANEL KIRI: pengisian data */}
        <div className="bg-white rounded-2xl border border-slate-200 p-5 lg:max-h-[calc(100vh-160px)] lg:overflow-y-auto custom-scroll" data-testid="letter-form-panel">
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-sm">Jenis Surat *</Label>
              <Select value={code} onValueChange={chooseTemplate} disabled={isView}>
                <SelectTrigger className="h-11" data-testid="letter-type-select"><SelectValue placeholder="Pilih jenis surat..." /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {templates.map((t) => (
                    <SelectItem key={t.code} value={t.code}>{t.name} {t.builtin ? "" : "•"}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {template && (
              <>
                {!isView && (
                  <>
                    <div className="space-y-1.5">
                      <Label className="text-sm">Nomor Urut Surat (opsional)</Label>
                      <Input value={nomor} onChange={(e) => setNomor(e.target.value)}
                        className="h-11" data-testid="letter-nomor-input"
                        placeholder="Kosongkan untuk nomor otomatis (mis. 001)" />
                      <p className="text-xs text-slate-400">Format: [nomor urut] / [kode desa 4 digit] / [bulan romawi] / [tahun].</p>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-sm">Ambil dari Data Warga (opsional)</Label>
                      <Select value={residentNik} onValueChange={pickResident}>
                        <SelectTrigger className="h-11" data-testid="letter-resident-select"><SelectValue placeholder="Isi otomatis dari warga..." /></SelectTrigger>
                        <SelectContent className="max-h-60">
                          {residents.map((r) => <SelectItem key={r.id} value={r.nik}>{r.full_name} — {r.nik}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </>
                )}

                <div className="space-y-3 border-t border-slate-100 pt-3">
                  {(template.fields || []).map((f) => {
                    if (tableTokenKeys.has(f.key)) return null; // diisi lewat editor tabel
                    if (isView) {
                      if (!String(values[f.key] || "").trim()) return null;
                      return (
                        <div key={f.key} className="space-y-1.5">
                          <Label className="text-sm text-slate-500">{f.label}</Label>
                          {renderInput(f)}
                        </div>
                      );
                    }
                    if (isRequiredField(f)) {
                      return (
                        <div key={f.key} className="space-y-1.5">
                          <Label className="text-sm">{f.label} *</Label>
                          {renderInput(f)}
                        </div>
                      );
                    }
                    const on = f.key in showOpt ? showOpt[f.key] : !!values[f.key];
                    const toggle = (v) => {
                      setShowOpt((s) => ({ ...s, [f.key]: v }));
                      if (!v) setValues((vv) => { const n = { ...vv }; delete n[f.key]; return n; });
                    };
                    return (
                      <div key={f.key} className="space-y-1.5 rounded-lg border border-slate-100 p-2.5">
                        <div className="flex items-center justify-between">
                          <Label className="text-sm text-slate-600">{f.label} <span className="text-slate-400">(opsional)</span></Label>
                          <Switch checked={on} onCheckedChange={toggle} data-testid={`field-toggle-${f.key}`} />
                        </div>
                        {on && renderInput(f)}
                      </div>
                    );
                  })}
                  {!isView && templateHasAddress(template) && (
                    <div className="space-y-3 rounded-lg bg-emerald-50/60 border border-emerald-100 p-3">
                      <p className="text-xs font-medium text-emerald-800">Wilayah (terisi otomatis dari Profil Desa)</p>
                      <div className="space-y-1.5">
                        <Label className="text-sm">{terms?.village_term || "Desa"}</Label>
                        <Input value={values.nama_desa || ""} onChange={(e) => setValues({ ...values, nama_desa: e.target.value })} className="h-11" data-testid="field-nama_desa" />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-sm">{terms?.district_term || "Kecamatan"}</Label>
                        <Input value={values.nama_kecamatan || ""} onChange={(e) => setValues({ ...values, nama_kecamatan: e.target.value })} className="h-11" data-testid="field-nama_kecamatan" />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-sm">Kabupaten/Kota</Label>
                        <Input value={values.nama_kabupaten || ""} onChange={(e) => setValues({ ...values, nama_kabupaten: e.target.value })} className="h-11" data-testid="field-nama_kabupaten" />
                      </div>
                    </div>
                  )}
                  {!isView && tableSpecs.map((spec, ti) => {
                    const rows = tableData[ti] || [];
                    const hasRes = spec.cols.some((c) => c.kind === "input" && isResidentBase(c.base));
                    return (
                      <div key={ti} className="space-y-2 rounded-lg border border-slate-200 p-3" data-testid={`table-editor-${ti}`}>
                        <div className="flex items-center justify-between gap-2">
                          <Label className="text-sm flex items-center gap-1.5"><Table size={15} className="text-[#064E3B]" /> Tabel: {spec.header.filter(Boolean).join(" / ")}</Label>
                          <Button type="button" variant="outline" size="sm" onClick={() => addRow(ti)} className="rounded-lg h-8" data-testid={`table-add-row-${ti}`}><Plus size={14} className="mr-1" /> Tambah Baris</Button>
                        </div>
                        {hasRes && <p className="text-[11px] text-slate-400">Kolom berisi data warga bisa diisi otomatis — pilih warga pada tiap baris.</p>}
                        {rows.length === 0 && <p className="text-sm text-slate-400">Belum ada baris. Klik &quot;Tambah Baris&quot;.</p>}
                        {rows.map((row, ri) => (
                          <div key={ri} className="rounded-md bg-slate-50 p-2.5 space-y-2" data-testid={`table-row-${ti}-${ri}`}>
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-xs font-medium text-slate-500">Baris {ri + 1}</span>
                              <div className="flex items-center gap-1">
                                {hasRes && (
                                  <Select value="" onValueChange={(nik) => fillRowFromResident(ti, ri, nik)}>
                                    <SelectTrigger className="h-8 w-44 bg-white text-xs" data-testid={`table-row-resident-${ti}-${ri}`}><SelectValue placeholder="Isi dari warga…" /></SelectTrigger>
                                    <SelectContent className="max-h-60">
                                      {residents.map((r) => <SelectItem key={r.id} value={r.nik}>{r.full_name} — {r.nik}</SelectItem>)}
                                    </SelectContent>
                                  </Select>
                                )}
                                <button type="button" onClick={() => removeRow(ti, ri)} data-testid={`table-del-row-${ti}-${ri}`} className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-600"><Trash2 size={15} /></button>
                              </div>
                            </div>
                            <div className="grid sm:grid-cols-2 gap-2">
                              {spec.cols.map((c, ci) => c.kind === "input" ? (
                                <div key={ci} className="space-y-1">
                                  <Label className="text-[11px] text-slate-500">{c.label}</Label>
                                  <Input value={row[ci] || ""} onChange={(e) => setCell(ti, ri, ci, e.target.value)} className="h-9 bg-white" data-testid={`table-cell-${ti}-${ri}-${ci}`} placeholder={c.label} />
                                </div>
                              ) : (
                                <div key={ci} className="space-y-1">
                                  <Label className="text-[11px] text-slate-400">{c.label}</Label>
                                  <div className="h-9 flex items-center px-2 text-xs text-slate-400 bg-slate-100 rounded-md">{c.kind === "auto" ? `otomatis (${ri + 1})` : c.value}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })}
                  {(template.fields || []).length === 0 && tableSpecs.length === 0 && <p className="text-sm text-slate-400">Template ini tidak memiliki item isian.</p>}
                </div>

                {Array.isArray(template.signers) && template.signers.length > 0 && (
                  <div className="space-y-3 rounded-lg border border-slate-200 p-3" data-testid="signer-editor">
                    <Label className="text-sm flex items-center gap-1.5"><Pencil size={15} className="text-[#064E3B]" /> Penandatangan</Label>
                    {template.signers.map((row, ri) => (
                      <div key={ri} className="space-y-2">
                        {row.heading ? <p className="text-[11px] font-medium text-slate-500">{row.heading}</p> : null}
                        <div className="grid sm:grid-cols-2 gap-2">
                          {(row.cells || []).map((c, ci) => {
                            const nk = c.name_key;
                            if (c.use_head) {
                              return (
                                <div key={ci} className="space-y-1">
                                  <Label className="text-[11px] text-slate-500">{c.label || "Penanda tangan"}</Label>
                                  <div className="h-9 flex items-center px-2 text-xs text-slate-400 bg-slate-100 rounded-md" data-testid={`signer-head-${ri}-${ci}`}>Otomatis: {profile?.village_head_name || "Kepala Desa"}</div>
                                </div>
                              );
                            }
                            if (!nk) return null;
                            const mode = signerMode[nk] || "manual";
                            return (
                              <div key={ci} className="space-y-1 rounded-md bg-slate-50 p-2">
                                <div className="flex items-center justify-between gap-2">
                                  <Label className="text-[11px] text-slate-500">{c.label || `Penanda tangan ${ci + 1}`}{c.jabatan ? ` — ${c.jabatan}` : ""}</Label>
                                  <div className="flex gap-0.5 bg-white rounded-md p-0.5 border border-slate-200 shrink-0">
                                    <button type="button" onClick={() => setSignerMode((m) => ({ ...m, [nk]: "manual" }))} data-testid={`signer-mode-manual-${nk}`}
                                      className={`text-[10px] px-1.5 py-0.5 rounded ${mode === "manual" ? "bg-[#064E3B] text-white" : "text-slate-500"}`}>Ketik</button>
                                    <button type="button" onClick={() => setSignerMode((m) => ({ ...m, [nk]: "warga" }))} data-testid={`signer-mode-warga-${nk}`}
                                      className={`text-[10px] px-1.5 py-0.5 rounded ${mode === "warga" ? "bg-[#064E3B] text-white" : "text-slate-500"}`}>Dari Warga</button>
                                  </div>
                                </div>
                                {mode === "warga" ? (
                                  <Select value="" onValueChange={(nik) => { const r = residents.find((x) => x.nik === nik); if (r) setValues((v) => ({ ...v, [nk]: r.full_name })); }}>
                                    <SelectTrigger className="h-9 bg-white text-xs" data-testid={`signer-warga-${nk}`}><SelectValue placeholder={values[nk] || "Pilih warga…"} /></SelectTrigger>
                                    <SelectContent className="max-h-60">{residents.map((r) => <SelectItem key={r.id} value={r.nik}>{r.full_name} — {r.nik}</SelectItem>)}</SelectContent>
                                  </Select>
                                ) : (
                                  <Input value={values[nk] || ""} onChange={(e) => setValues((v) => ({ ...v, [nk]: e.target.value }))} className="h-9 bg-white" data-testid={`signer-name-${nk}`} placeholder="Nama penanda tangan" />
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {!isView && (
                  <Button onClick={submit} disabled={saving} data-testid="letter-generate-submit" className="w-full bg-[#064E3B] hover:bg-[#043E2F] rounded-xl h-11">
                    {saving && <Loader2 className="animate-spin mr-2" size={16} />} Terbitkan Surat
                  </Button>
                )}
              </>
            )}
          </div>
        </div>

        {/* PANEL KANAN: pratinjau langsung */}
        <div className="space-y-3 lg:sticky lg:top-4">
          <style>{`@page { size: ${paper === "F4" ? "215mm 330mm" : "210mm 297mm"}; margin: 0; }`}</style>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 bg-white rounded-2xl border border-slate-200 p-3 no-print">
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-500">Ukuran:</span>
              <div className="flex gap-1 bg-slate-100 p-1 rounded-lg" data-testid="paper-size-toggle">
                {["A4", "F4"].map((p) => (
                  <button key={p} onClick={() => setPaper(p)} data-testid={`paper-${p}`}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition ${paper === p ? "bg-white shadow text-[#064E3B]" : "text-slate-500"}`}>
                    {p}{p === "F4" ? " (Folio)" : ""}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-500">Spasi isi:</span>
              <div className="flex gap-1 bg-slate-100 p-1 rounded-lg" data-testid="spacing-toggle">
                {[{ v: 1.0, l: "1,0" }, { v: 1.15, l: "1,15" }, { v: 1.5, l: "1,5" }, { v: 2.0, l: "2,0" }].map((s) => (
                  <button key={s.v} onClick={() => setLineSpacing(s.v)} data-testid={`spacing-${s.v}`}
                    className={`px-2.5 py-1.5 rounded-md text-sm font-medium transition ${lineSpacing === s.v ? "bg-white shadow text-[#064E3B]" : "text-slate-500"}`}>
                    {s.l}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-500">Font:</span>
              <div className="flex gap-1 bg-slate-100 p-1 rounded-lg" data-testid="fontsize-toggle">
                {[10, 11, 12, 13].map((f) => (
                  <button key={f} onClick={() => setFontPt(f)} data-testid={`fontsize-${f}`}
                    className={`px-2.5 py-1.5 rounded-md text-sm font-medium transition ${fontPt === f ? "bg-white shadow text-[#064E3B]" : "text-slate-500"}`}>
                    {f}
                  </button>
                ))}
              </div>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <Switch checked={showKop} onCheckedChange={setShowKop} data-testid="kop-toggle" />
              <span className="text-sm text-slate-500">KOP</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <Switch checked={showKopAddress} onCheckedChange={setShowKopAddress} disabled={!showKop} data-testid="kop-address-toggle" />
              <span className="text-sm text-slate-500">Alamat kop</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <Switch checked={showNumber} onCheckedChange={setShowNumber} data-testid="number-toggle" />
              <span className="text-sm text-slate-500">Nomor</span>
            </label>
            <Button onClick={handlePrint} disabled={!previewDoc} data-testid="letter-print-button" className="ml-auto bg-[#D97706] hover:bg-[#B45309] rounded-xl">
              <Printer size={18} className="mr-1.5" /> Cetak / Print
            </Button>
          </div>

          <div className="bg-slate-100 rounded-2xl border border-slate-200 p-4 overflow-auto custom-scroll lg:max-h-[calc(100vh-230px)]" data-testid="letter-preview-panel">
            {previewDoc ? (
              <div className="shadow-lg mx-auto bg-white relative" style={{ width: "100%", maxWidth: paper === "F4" ? 813 : 794 }}>
                {previewing && <div className="absolute top-2 right-2 z-10 bg-white/80 rounded-full p-1"><Loader2 className="animate-spin text-[#064E3B]" size={16} /></div>}
                <FitScale width={paper === "F4" ? 813 : 794}>
                  <LetterDocument letter={previewDoc} profile={profile} terms={terms} paper={paper} showKopAddress={showKopAddress} showKop={showKop} showNumber={showNumber} lineSpacing={lineSpacing} fontPt={fontPt} />
                </FitScale>
              </div>
            ) : (
              <div className="py-24 text-center text-slate-400">
                <FileText className="mx-auto mb-3 opacity-40" size={40} />
                <p className="text-sm">Pilih jenis surat & isi data untuk melihat pratinjau langsung.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Daftar surat terbit ---------------- */
function LettersList({ loading, items, onPreview, onRemove }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
      {loading ? (
        <div className="p-16 grid place-items-center"><Loader2 className="animate-spin text-[#064E3B]" size={28} /></div>
      ) : items.length === 0 ? (
        <div className="p-16 text-center">
          <div className="h-14 w-14 rounded-2xl bg-emerald-50 grid place-items-center mx-auto mb-4"><FileText className="text-[#064E3B]" /></div>
          <p className="text-slate-500">Belum ada surat diterbitkan.</p>
        </div>
      ) : (
        <div className="overflow-x-auto custom-scroll">
          <table className="w-full text-sm" data-testid="letter-table">
            <thead>
              <tr className="text-left text-slate-500 border-b border-slate-200 bg-slate-50">
                <th className="px-4 py-3 font-semibold">Nomor Surat</th>
                <th className="px-4 py-3 font-semibold">Jenis</th>
                <th className="px-4 py-3 font-semibold">Nama Pemohon</th>
                <th className="px-4 py-3 font-semibold">Keperluan</th>
                <th className="px-4 py-3 font-semibold text-right">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {items.map((l) => (
                <tr key={l.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`letter-row-${l.id}`}>
                  <td className="px-4 py-3 font-mono text-slate-700">{l.letter_number}</td>
                  <td className="px-4 py-3 text-slate-600">{l.letter_type_name}</td>
                  <td className="px-4 py-3 font-medium text-slate-900">{l.resident_name}</td>
                  <td className="px-4 py-3 text-slate-600 max-w-[200px] truncate">{l.purpose}</td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      <button onClick={() => onPreview(l)} data-testid={`letter-preview-${l.id}`} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-[#064E3B]"><Eye size={16} /></button>
                      <button onClick={() => onRemove(l.id)} data-testid={`letter-delete-${l.id}`} className="p-2 rounded-lg hover:bg-red-50 text-slate-500 hover:text-red-600"><Trash2 size={16} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ---------------- Panel template ---------------- */
function TemplatesPanel({ templates, onChanged, profile, terms }) {
  const [learnOpen, setLearnOpen] = useState(false);
  const [editing, setEditing] = useState(null); // template object or {} for new

  const removeTpl = async (t) => {
    if (!window.confirm(`Hapus template "${t.name}"?`)) return;
    try { await api.delete(`/letters/templates/${t.id}`); toast.success("Template dihapus"); onChanged(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3">
        <Button onClick={() => setLearnOpen(true)} data-testid="template-learn-button" className="bg-[#064E3B] hover:bg-[#043E2F] rounded-xl h-11">
          <Sparkles size={18} className="mr-1.5" /> Unggah &amp; Pelajari Surat
        </Button>
        <Button onClick={() => setEditing({ name: "", category: "Keterangan", description: "", fields: [], body_template: "" })}
          variant="outline" data-testid="template-manual-button" className="rounded-xl h-11 border-slate-300">
          <Plus size={18} className="mr-1.5" /> Buat Template Manual
        </Button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="template-grid">
        {templates.map((t) => (
          <div key={t.id} className="bg-white rounded-2xl border border-slate-200 p-4 flex flex-col" data-testid={`template-card-${t.code}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="h-10 w-10 rounded-xl bg-emerald-50 grid place-items-center shrink-0"><FileText size={18} className="text-[#064E3B]" /></div>
              {t.builtin ? (
                <Badge variant="secondary" className="bg-slate-100 text-slate-500">Bawaan</Badge>
              ) : (
                <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">{t.source === "ai" ? "AI" : "Kustom"}</Badge>
              )}
            </div>
            <div className="mt-3 font-semibold text-slate-900 leading-snug">{t.name}</div>
            <div className="text-xs text-slate-400 font-mono mt-0.5">{t.code}</div>
            <p className="text-sm text-slate-500 mt-1.5 line-clamp-2 flex-1">{t.description || "—"}</p>
            <div className="text-xs text-slate-400 mt-2">{(t.fields || []).length} item isian</div>
            {!t.builtin && (
              <div className="flex gap-1 mt-3 pt-3 border-t border-slate-100">
                <button onClick={() => setEditing(t)} data-testid={`template-edit-${t.code}`} className="flex-1 py-1.5 rounded-lg text-sm text-slate-600 hover:bg-slate-100 flex items-center justify-center gap-1"><Pencil size={14} /> Ubah</button>
                <button onClick={() => removeTpl(t)} data-testid={`template-delete-${t.code}`} className="flex-1 py-1.5 rounded-lg text-sm text-red-600 hover:bg-red-50 flex items-center justify-center gap-1"><Trash2 size={14} /> Hapus</button>
              </div>
            )}
          </div>
        ))}
      </div>

      <LearnDialog open={learnOpen} onOpenChange={setLearnOpen}
        onDraft={(draft) => { setLearnOpen(false); setEditing(draft); }} />
      {editing && (
        <TemplateEditor template={editing} profile={profile} terms={terms} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); onChanged(); }} />
      )}
    </div>
  );
}

/* ---------------- Unggah & Pelajari (AI) ---------------- */
function LearnDialog({ open, onOpenChange, onDraft }) {
  const [busy, setBusy] = useState(false);
  const [files, setFiles] = useState([]);
  const inputRef = useRef(null);

  const addFiles = (fileList) => {
    const arr = Array.from(fileList || []);
    if (!arr.length) return;
    setFiles((f) => {
      const next = [...f, ...arr];
      if (next.length > 10) { toast.error("Maksimal 10 file/halaman sekali pelajari"); return next.slice(0, 10); }
      return next;
    });
    if (inputRef.current) inputRef.current.value = "";
  };
  const removeFile = (i) => setFiles((f) => f.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (!files.length || busy) return;
    setBusy(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const r = await api.post("/letters/templates/learn", fd);
      toast.success("Surat berhasil dipelajari — periksa & simpan template");
      setFiles([]);
      onDraft({ ...r.data, source: r.data.source || "ai" });
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); if (inputRef.current) inputRef.current.value = ""; }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="max-w-md" data-testid="learn-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-2"><Sparkles size={20} className="text-[#064E3B]" /> Unggah &amp; Pelajari Surat</DialogTitle>
          <DialogDescription>Unggah contoh surat (PDF, Word .docx, atau foto). <b>Bisa beberapa file sekaligus untuk surat 2–3+ halaman</b> — susun urut sesuai nomor halaman. Sistem mempelajari SEMUA halaman menjadi satu template utuh.</DialogDescription>
        </DialogHeader>
        <div className="py-2 space-y-3">
          <label
            className={`block border-2 border-dashed rounded-2xl p-6 text-center cursor-pointer transition ${busy ? "opacity-60 pointer-events-none" : "hover:border-[#064E3B] hover:bg-emerald-50/40"} border-slate-300`}
            data-testid="learn-dropzone">
            <input ref={inputRef} type="file" className="hidden" accept=".pdf,.docx,.txt,.png,.jpg,.jpeg,.webp" multiple
              onChange={(e) => addFiles(e.target.files)} data-testid="learn-file-input" />
            <div className="flex flex-col items-center gap-2 text-slate-500">
              <div className="h-12 w-12 rounded-2xl bg-emerald-50 grid place-items-center"><Upload className="text-[#064E3B]" /></div>
              <span className="text-sm font-medium text-slate-700">Klik untuk pilih file</span>
              <span className="text-xs">PDF (multi-halaman) · Word (.docx) · Foto per halaman (JPG/PNG) — bisa pilih banyak</span>
            </div>
          </label>
          {files.length > 0 && (
            <div className="space-y-1.5" data-testid="learn-file-list">
              {files.map((f, i) => (
                <div key={i} className="flex items-center gap-2 rounded-lg border border-slate-200 px-2.5 py-1.5 bg-slate-50" data-testid={`learn-file-${i}`}>
                  <span className="text-[11px] font-semibold text-slate-400 shrink-0 w-14">Halaman {i + 1}</span>
                  <span className="text-xs text-slate-600 truncate flex-1">{f.name}</span>
                  <button type="button" onClick={() => removeFile(i)} disabled={busy}
                    className="p-1 rounded hover:bg-red-50 text-slate-400 hover:text-red-600 shrink-0" data-testid={`learn-file-del-${i}`}><X size={13} /></button>
                </div>
              ))}
              <p className="text-[11px] text-slate-400">Urutan atas → bawah = urutan halaman surat. Hapus &amp; pilih ulang bila urutan salah.</p>
            </div>
          )}
          <Button onClick={submit} disabled={busy || files.length === 0} data-testid="learn-submit"
            className="w-full bg-[#064E3B] hover:bg-[#043E2F] rounded-xl h-11">
            {busy ? (<><Loader2 className="animate-spin mr-2" size={16} /> Mempelajari {files.length} file — bisa 1–3 menit…</>) : (<>Pelajari {files.length > 0 ? `${files.length} File` : "Surat"}</>)}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Editor template (manual & hasil AI) ---------------- */
const FIELD_TYPES = [
  { v: "text", l: "Teks singkat" }, { v: "textarea", l: "Teks panjang" },
  { v: "date", l: "Tanggal" }, { v: "number", l: "Angka" },
];
const CONTEXT_TOKENS = [
  "nomor_surat", "tanggal", "nama_kepala", "sebutan_kepala", "sebutan_desa",
  "nama_desa", "sebutan_kecamatan", "nama_kecamatan", "nama_kabupaten", "nama_provinsi",
];

function slugify(s) {
  return (s || "").toLowerCase().trim().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
}

// Token yang bisa dipakai pada FORMAT NOMOR SURAT (chip diklik untuk disisipkan).
const NUMBER_TOKENS = [
  "nomor", "kode_surat", "kode_desa", "bulan_romawi", "bulan", "tahun", "tahun2", "nama_desa", "nama_kecamatan",
];
const DEFAULT_NUMBER_FORMAT = "{nomor} / {kode_desa} / {bulan_romawi} / {tahun}";
const _ROMAN = ["", "I", "II", "III", "IV", "V", "VI", "VII", "VIII", "IX", "X", "XI", "XII"];

// Render CONTOH nomor surat dari format bertoken (untuk pratinjau setelan format).
function renderNumberExample(fmt, profile, code) {
  const now = new Date();
  const vc = ((profile?.village_code || "").replace(/\D/g, "").slice(-4)) || (code || "SKU");
  const tok = {
    nomor: "001",
    kode_surat: code || "SKU",
    kode_desa: vc,
    bulan_romawi: _ROMAN[now.getMonth() + 1],
    bulan: String(now.getMonth() + 1).padStart(2, "0"),
    tahun: String(now.getFullYear()),
    tahun2: String(now.getFullYear() % 100).padStart(2, "0"),
    nama_desa: profile?.village_name || "",
    nama_kecamatan: profile?.district_name || "",
  };
  let out = fmt || DEFAULT_NUMBER_FORMAT;
  Object.entries(tok).forEach(([k, v]) => { out = out.split(`{${k}}`).join(v); });
  return out.trim();
}

// Isi token pada body_template untuk PRATINJAU template (bukan surat final).
// Token konteks -> nilai profil desa; token field -> penanda «Label» agar terlihat bagian variabel.
function fillPreviewBody(body, fields, profile, terms) {
  const t = terms || {};
  const ctx = {
    tanggal: new Date().toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" }),
    nomor_surat: "",
    nama_kepala: profile?.village_head_name || "",
    sebutan_kepala: t.head_term || "Kepala Desa",
    sebutan_desa: t.village_term || "Desa",
    nama_desa: profile?.village_name || "",
    sebutan_kecamatan: t.district_term || "Kecamatan",
    nama_kecamatan: profile?.district_name || "",
    nama_kabupaten: profile?.regency_name || "",
    nama_provinsi: profile?.province_name || "",
  };
  return (body || "").replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (m, key) => {
    if (ctx[key] !== undefined && ctx[key] !== "") return ctx[key];
    const f = (fields || []).find((x) => x.key === key);
    return `«${f?.label || key}»`;
  });
}

function TemplateEditor({ template, onClose, onSaved, profile, terms }) {
  const isNew = !template.id;
  const [name, setName] = useState(template.name || "");
  const [category, setCategory] = useState(template.category || "Keterangan");
  const [description, setDescription] = useState(template.description || "");
  const [fields, setFields] = useState(template.fields || []);
  const [body, setBody] = useState(template.body_template || "");
  const [hasKop, setHasKop] = useState(template.has_kop !== false);
  const [hasNumber, setHasNumber] = useState(template.has_number !== false);
  const [numberFormat, setNumberFormat] = useState(template.number_format || DEFAULT_NUMBER_FORMAT);
  const [signers, setSigners] = useState(Array.isArray(template.signers) ? template.signers : []);
  const [signersCustom, setSignersCustom] = useState(Array.isArray(template.signers) && template.signers.length > 0);
  const newSignerCell = () => ({ label: "", jabatan: "", use_head: false, fixed_name: "", name_key: "", show_place_date: false, place_date_text: "" });
  const addSignerRow = () => setSigners((s) => [...s, { heading: "", cells: [newSignerCell()] }]);
  const removeSignerRow = (ri) => setSigners((s) => s.filter((_, i) => i !== ri));
  const setSignerRow = (ri, patch) => setSigners((s) => s.map((r, i) => (i === ri ? { ...r, ...patch } : r)));
  const addSignerCell = (ri) => setSigners((s) => s.map((r, i) => (i === ri ? { ...r, cells: [...(r.cells || []), newSignerCell()] } : r)));
  const removeSignerCell = (ri, ci) => setSigners((s) => s.map((r, i) => (i === ri ? { ...r, cells: (r.cells || []).filter((_, j) => j !== ci) } : r)));
  const setSignerCell = (ri, ci, patch) => setSigners((s) => s.map((r, i) => (i === ri ? { ...r, cells: (r.cells || []).map((c, j) => (j === ci ? { ...c, ...patch } : c)) } : r)));
  // Atur jumlah kolom (penanda tangan sejajar) pada satu baris: 1, 2, 3, ...
  const setSignerColCount = (ri, n) => setSigners((s) => s.map((r, i) => {
    if (i !== ri) return r;
    const cells = [...(r.cells || [])];
    while (cells.length < n) cells.push(newSignerCell());
    return { ...r, cells: cells.slice(0, Math.max(1, n)) };
  }));
  const [saving, setSaving] = useState(false);
  const bodyRef = useRef(null);
  const numRef = useRef(null);

  const addField = () => setFields((f) => [...f, { key: "", label: "", type: "text", required: false }]);
  const updField = (i, patch) => setFields((f) => f.map((x, idx) => idx === i ? { ...x, ...patch } : x));
  const delField = (i) => setFields((f) => f.filter((_, idx) => idx !== i));

  // Sisipkan teks pada posisi kursor di editor isi surat (untuk tombol Tab / Alinea).
  const insertAtCursor = (text) => {
    const el = bodyRef.current;
    if (!el) { setBody((b) => b + text); return; }
    const start = el.selectionStart ?? body.length;
    const end = el.selectionEnd ?? body.length;
    const next = body.slice(0, start) + text + body.slice(end);
    setBody(next);
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + text.length;
      el.setSelectionRange(pos, pos);
    });
  };
  // Sisipkan token nomor {token} pada posisi kursor di input format nomor surat.
  const insertNumberToken = (tok) => {
    const el = numRef.current;
    const ins = `{${tok}}`;
    if (!el) { setNumberFormat((v) => (v || "") + ins); return; }
    const start = el.selectionStart ?? (numberFormat || "").length;
    const end = el.selectionEnd ?? (numberFormat || "").length;
    const next = (numberFormat || "").slice(0, start) + ins + (numberFormat || "").slice(end);
    setNumberFormat(next);
    requestAnimationFrame(() => { el.focus(); const p = start + ins.length; el.setSelectionRange(p, p); });
  };
  // Tekan Tab di dalam editor -> sisipkan karakter tab; tekan Enter pada baris bernomor -> lanjut otomatis.
  const onBodyKeyDown = (e) => {
    if (e.key === "Tab") {
      e.preventDefault();
      insertAtCursor("\t");
      return;
    }
    if (e.key === "Enter" && !e.shiftKey) {
      const el = bodyRef.current;
      if (!el) return;
      const start = el.selectionStart ?? 0;
      const before = body.slice(0, start);
      const lineStart = before.lastIndexOf("\n") + 1;
      const curLine = before.slice(lineStart);
      const m = curLine.match(/^(\s*)(\d+)([.)])\s+(.*)$/);
      if (!m) return;
      e.preventDefault();
      const indent = m[1], num = m[2], sep = m[3], content = m[4];
      if (content.trim() === "") {
        // Baris bernomor kosong -> keluar dari daftar (hapus penanda nomor).
        const next = body.slice(0, lineStart) + body.slice(start);
        setBody(next);
        requestAnimationFrame(() => { el.focus(); el.setSelectionRange(lineStart, lineStart); });
      } else {
        // Lanjutkan daftar dengan nomor berikutnya secara otomatis.
        insertAtCursor(`\n${indent}${parseInt(num, 10) + 1}${sep} `);
      }
    }
  };

  const save = async () => {
    if (!name.trim()) { toast.error("Nama template wajib diisi"); return; }
    if (!body.trim()) { toast.error("Isi surat (body) wajib diisi"); return; }
    const cleanFields = fields
      .filter((f) => f.label?.trim())
      .map((f) => ({ ...f, key: f.key?.trim() ? slugify(f.key) : slugify(f.label) }));
    const payload = { name, category, description, fields: cleanFields, body_template: body, has_kop: hasKop, has_number: hasNumber, number_format: (numberFormat || DEFAULT_NUMBER_FORMAT), source: template.source || "manual", signers: signersCustom && signers.length ? signers : null };
    setSaving(true);
    try {
      if (isNew) await api.post("/letters/templates", payload);
      else await api.put(`/letters/templates/${template.id}`, payload);
      toast.success("Template disimpan");
      onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  // Objek "surat" tiruan untuk pratinjau langsung.
  const previewSigners = (signersCustom && signers.length)
    ? signers.map((row) => ({
        heading: row.heading || "",
        cells: (row.cells || []).map((c) => ({
          label: c.label || "",
          jabatan: c.jabatan || "",
          show_place_date: !!c.show_place_date,
          place_date_text: c.place_date_text || "",
          name: c.use_head ? (profile?.village_head_name || "") : (c.fixed_name || ""),
        })),
      }))
    : null;
  const previewLetter = {
    letter_type_name: name || "JUDUL SURAT",
    letter_number: renderNumberExample(numberFormat, profile, template.code),
    body_rendered: fillPreviewBody(body, fields, profile, terms),
    signers: previewSigners,
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-6xl w-[95vw] max-h-[94vh] overflow-hidden p-0" data-testid="template-editor">
        <div className="grid lg:grid-cols-2 gap-0 max-h-[94vh]">
          {/* Kolom kiri: form */}
          <div className="overflow-y-auto custom-scroll p-6 space-y-4">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">{isNew ? "Buat Template Surat" : "Ubah Template"}</DialogTitle>
              <DialogDescription>Atur item isian &amp; isi surat. Gunakan token {"{{nama_field}}"} pada isi surat. Pratinjau di kanan diperbarui otomatis.</DialogDescription>
            </DialogHeader>

            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-sm">Nama Surat *</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} className="h-11" data-testid="tpl-name" placeholder="cth: Surat Keterangan Usaha" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">Kategori</Label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger className="h-11" data-testid="tpl-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["Keterangan", "Pengantar", "Izin", "Lainnya"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm">Deskripsi</Label>
              <Input value={description} onChange={(e) => setDescription(e.target.value)} className="h-11" data-testid="tpl-desc" />
            </div>
            <div className="flex items-center justify-between rounded-xl border border-slate-200 px-3 py-2.5">
              <div>
                <Label className="text-sm">Pakai KOP / Kepala Surat</Label>
                <p className="text-[11px] text-slate-400">Matikan untuk surat tanpa kop (mis. perjanjian / berita acara).</p>
              </div>
              <Switch checked={hasKop} onCheckedChange={setHasKop} data-testid="tpl-has-kop" />
            </div>

            {/* Nomor Surat: on/off + format bertoken (default seperti sekarang) */}
            <div className="rounded-xl border border-slate-200 px-3 py-2.5 space-y-2.5">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm">Pakai Nomor Surat</Label>
                  <p className="text-[11px] text-slate-400">Matikan untuk surat tanpa nomor (mis. beberapa surat pernyataan).</p>
                </div>
                <Switch checked={hasNumber} onCheckedChange={setHasNumber} data-testid="tpl-has-number" />
              </div>
              {hasNumber && (
                <div className="space-y-1.5">
                  <Label className="text-xs text-slate-500">Format Nomor Surat</Label>
                  <Input ref={numRef} value={numberFormat} onChange={(e) => setNumberFormat(e.target.value)}
                    className="h-10 font-mono text-sm" data-testid="tpl-number-format" placeholder={DEFAULT_NUMBER_FORMAT} />
                  <div className="flex flex-wrap gap-1">
                    <span className="text-[11px] text-slate-400 mr-1">Elemen:</span>
                    {NUMBER_TOKENS.map((t) => (
                      <button key={t} type="button" onClick={() => insertNumberToken(t)} data-testid={`tpl-num-token-${t}`}
                        className="text-[11px] font-mono px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 hover:bg-emerald-100 hover:text-[#064E3B]">{`{${t}}`}</button>
                    ))}
                    <button type="button" onClick={() => setNumberFormat(DEFAULT_NUMBER_FORMAT)} data-testid="tpl-num-reset"
                      className="text-[11px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 hover:bg-amber-100">Reset default</button>
                  </div>
                  <p className="text-[11px] text-slate-500">Contoh: <span className="font-mono text-slate-700" data-testid="tpl-number-example">{renderNumberExample(numberFormat, profile, template.code)}</span></p>
                </div>
              )}
            </div>

            {/* Blok Tanda Tangan: default Kepala Desa di kanan, atau susun kustom multi-baris/multi-kolom */}
            <div className="rounded-xl border border-slate-200 px-3 py-2.5 space-y-3" data-testid="tpl-signers-section">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <Label className="text-sm">Atur Blok Tanda Tangan</Label>
                  <p className="text-[11px] text-slate-400">Default: satu tanda tangan {terms?.head_term || "Kepala Desa"} di kanan. Aktifkan untuk surat multi-pihak (2/3+ penanda tangan, baris «Mengetahui», dst) sesuai format surat asli.</p>
                </div>
                <Switch checked={signersCustom} onCheckedChange={(v) => { setSignersCustom(v); if (v && signers.length === 0) addSignerRow(); }} data-testid="tpl-signers-custom" />
              </div>
              {signersCustom && (
                <div className="space-y-3">
                  {signers.map((row, ri) => (
                    <div key={ri} className="rounded-lg bg-slate-50 border border-slate-200 p-2.5 space-y-2" data-testid={`tpl-signer-row-${ri}`}>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[11px] font-semibold text-slate-500 shrink-0">Baris {ri + 1}</span>
                        <Input value={row.heading || ""} onChange={(e) => setSignerRow(ri, { heading: e.target.value })}
                          placeholder="Pengantar baris (opsional) — cth: Mengetahui," className="h-8 flex-1 min-w-[150px] bg-white text-xs" data-testid={`tpl-signer-heading-${ri}`} />
                        <div className="flex items-center gap-0.5 shrink-0">
                          <span className="text-[11px] text-slate-400 mr-0.5">Kolom:</span>
                          {[1, 2, 3, 4].map((n) => (
                            <button key={n} type="button" onClick={() => setSignerColCount(ri, n)} data-testid={`tpl-signer-cols-${ri}-${n}`}
                              className={`text-[11px] w-6 h-6 rounded ${(row.cells || []).length === n ? "bg-[#064E3B] text-white" : "bg-white border border-slate-200 text-slate-500 hover:border-[#064E3B]"}`}>{n}</button>
                          ))}
                        </div>
                        <button type="button" onClick={() => removeSignerRow(ri)} title="Hapus baris"
                          className="p-1 rounded hover:bg-red-50 text-slate-400 hover:text-red-600 shrink-0" data-testid={`tpl-signer-del-row-${ri}`}><Trash2 size={14} /></button>
                      </div>
                      <div className="grid sm:grid-cols-2 gap-2">
                        {(row.cells || []).map((c, ci) => (
                          <div key={ci} className="rounded-md bg-white border border-slate-200 p-2 space-y-1.5" data-testid={`tpl-signer-cell-${ri}-${ci}`}>
                            <div className="flex items-center justify-between">
                              <span className="text-[11px] font-medium text-slate-500">Kolom {ci + 1}</span>
                              {(row.cells || []).length > 1 && (
                                <button type="button" onClick={() => removeSignerCell(ri, ci)} title="Hapus kolom"
                                  className="p-0.5 rounded hover:bg-red-50 text-slate-300 hover:text-red-600" data-testid={`tpl-signer-del-cell-${ri}-${ci}`}><X size={13} /></button>
                              )}
                            </div>
                            <Input value={c.label || ""} onChange={(e) => setSignerCell(ri, ci, { label: e.target.value })}
                              placeholder="Label peran (cth: PIHAK PERTAMA)" className="h-8 text-xs bg-white" data-testid={`tpl-signer-label-${ri}-${ci}`} />
                            <Input value={c.jabatan || ""} onChange={(e) => setSignerCell(ri, ci, { jabatan: e.target.value })}
                              placeholder="Jabatan (cth: Kepala Desa / Direktur)" className="h-8 text-xs bg-white" data-testid={`tpl-signer-jabatan-${ri}-${ci}`} />
                            <label className="flex items-center gap-1.5 text-[11px] text-slate-500 cursor-pointer">
                              <input type="checkbox" checked={!!c.use_head} onChange={(e) => setSignerCell(ri, ci, { use_head: e.target.checked })} data-testid={`tpl-signer-usehead-${ri}-${ci}`} />
                              Nama otomatis: {terms?.head_term || "Kepala Desa"} ({profile?.village_head_name || "…"})
                            </label>
                            {!c.use_head && (
                              <Input value={c.fixed_name || ""} onChange={(e) => setSignerCell(ri, ci, { fixed_name: e.target.value })}
                                placeholder="Nama bawaan (opsional; bisa ketik/pilih warga saat buat surat)" className="h-8 text-xs bg-white" data-testid={`tpl-signer-fixed-${ri}-${ci}`} />
                            )}
                            <label className="flex items-center gap-1.5 text-[11px] text-slate-500 cursor-pointer">
                              <input type="checkbox" checked={!!c.show_place_date} onChange={(e) => setSignerCell(ri, ci, { show_place_date: e.target.checked })} data-testid={`tpl-signer-pd-${ri}-${ci}`} />
                              Tempat &amp; tanggal di kolom ini
                            </label>
                            {c.show_place_date && (
                              <Input value={c.place_date_text || ""} onChange={(e) => setSignerCell(ri, ci, { place_date_text: e.target.value })}
                                placeholder={`Kosongkan = otomatis: ${profile?.village_name || "Desa"}, <tanggal>`} className="h-8 text-xs bg-white" data-testid={`tpl-signer-pdtext-${ri}-${ci}`} />
                            )}
                          </div>
                        ))}
                      </div>
                      {(row.cells || []).length >= 1 && (
                        <button type="button" onClick={() => addSignerCell(ri)} data-testid={`tpl-signer-add-cell-${ri}`}
                          className="text-[11px] px-2 h-6 rounded bg-white border border-slate-200 text-slate-500 hover:border-[#064E3B] hover:text-[#064E3B]">+ Kolom</button>
                      )}
                    </div>
                  ))}
                  <Button type="button" variant="outline" size="sm" onClick={addSignerRow} className="rounded-lg h-8" data-testid="tpl-signer-add-row">
                    <Plus size={14} className="mr-1" /> Tambah Baris Tanda Tangan
                  </Button>
                  <p className="text-[11px] text-slate-400">Tiap baris punya jumlah kolom sendiri (mis. baris 1 dua pihak berdampingan, baris 2 satu «Mengetahui» di tengah). Nama penanda tangan diisi saat membuat surat — bisa diketik manual atau dipilih dari Data Warga.</p>
                </div>
              )}
            </div>

            {/* Fields */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm">Item Isian ({fields.length})</Label>
                <Button type="button" variant="outline" size="sm" onClick={addField} className="rounded-lg h-8" data-testid="tpl-add-field"><Plus size={14} className="mr-1" /> Tambah Item</Button>
              </div>
              <div className="space-y-2">
                {fields.map((f, i) => (
                  <div key={i} className="flex flex-wrap items-center gap-2 bg-slate-50 rounded-xl p-2" data-testid={`tpl-field-${i}`}>
                    <Input value={f.label} onChange={(e) => updField(i, { label: e.target.value, key: f.key || slugify(e.target.value) })}
                      placeholder="Label (mis. Nama Usaha)" className="h-9 flex-1 min-w-[140px] bg-white" data-testid={`tpl-field-label-${i}`} />
                    <Input value={f.key} onChange={(e) => updField(i, { key: slugify(e.target.value) })}
                      placeholder="token" className="h-9 w-32 bg-white font-mono text-xs" />
                    <Select value={f.type} onValueChange={(v) => updField(i, { type: v })}>
                      <SelectTrigger className="h-9 w-36 bg-white"><SelectValue /></SelectTrigger>
                      <SelectContent>{FIELD_TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
                    </Select>
                    <label className="flex items-center gap-1 text-xs text-slate-500 px-1">
                      <input type="checkbox" checked={!!f.required} onChange={(e) => updField(i, { required: e.target.checked })} /> wajib
                    </label>
                    <button onClick={() => insertAtCursor(`{{${f.key || slugify(f.label)}}}`)} title="Sisipkan token ke isi surat"
                      className="p-1.5 rounded-lg hover:bg-emerald-50 text-slate-400 hover:text-[#064E3B]" data-testid={`tpl-field-insert-${i}`}><CornerDownLeft size={16} /></button>
                    <button onClick={() => delField(i)} className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-600"><X size={16} /></button>
                  </div>
                ))}
                {fields.length === 0 && <p className="text-sm text-slate-400 px-1">Belum ada item. Tambahkan item isian surat.</p>}
              </div>
            </div>

            {/* Body */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <Label className="text-sm">Isi Surat (body) *</Label>
                <div className="flex items-center gap-1">
                  <button type="button" onClick={() => insertAtCursor("\t")} data-testid="tpl-btn-tab"
                    className="text-xs px-2 h-7 rounded-lg bg-slate-100 text-slate-600 hover:bg-emerald-100 hover:text-[#064E3B] flex items-center gap-1"><Indent size={13} /> Tab</button>
                  <button type="button" onClick={() => insertAtCursor("\n\n\t")} data-testid="tpl-btn-alinea"
                    className="text-xs px-2 h-7 rounded-lg bg-slate-100 text-slate-600 hover:bg-emerald-100 hover:text-[#064E3B] flex items-center gap-1"><Pilcrow size={13} /> Alinea</button>
                  <button type="button" onClick={() => insertAtCursor("\n1. ")} data-testid="tpl-btn-poin" title="Daftar bernomor otomatis (Enter untuk poin berikutnya)"
                    className="text-xs px-2 h-7 rounded-lg bg-slate-100 text-slate-600 hover:bg-emerald-100 hover:text-[#064E3B] flex items-center gap-1"><ListOrdered size={13} /> Poin</button>
                  <button type="button" onClick={() => insertAtCursor("\n[[TABLE]]\nNo | Uraian | Keterangan\n1 | {{uraian_1}} | {{keterangan_1}}\n[[/TABLE]]\n")} data-testid="tpl-btn-table"
                    className="text-xs px-2 h-7 rounded-lg bg-slate-100 text-slate-600 hover:bg-emerald-100 hover:text-[#064E3B] flex items-center gap-1"><Table size={13} /> Tabel</button>
                </div>
              </div>
              <Textarea ref={bodyRef} value={body} onChange={(e) => setBody(e.target.value)} onKeyDown={onBodyKeyDown} rows={10} className="font-mono text-sm" data-testid="tpl-body"
                placeholder="Gunakan token seperti {{nama}}, {{keperluan}}, {{sebutan_kepala}}, {{nama_desa}} ..." />
              <p className="text-[11px] text-slate-400">Tips: tombol <b>Tab</b> untuk meratakan daftar identitas (Label ⭾ nilai). Tombol <b>Alinea</b> untuk paragraf baru menjorok. Tombol <b>Poin</b> membuat daftar bernomor otomatis — tekan <b>Enter</b> untuk poin berikutnya, Enter di poin kosong untuk berhenti.</p>
              <div className="flex flex-wrap gap-1 pt-1">
                <span className="text-xs text-slate-400 mr-1">Token konteks:</span>
                {CONTEXT_TOKENS.map((t) => (
                  <button key={t} type="button" onClick={() => insertAtCursor(`{{${t}}}`)}
                    className="text-[11px] font-mono px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 hover:bg-emerald-100 hover:text-[#064E3B]">{`{{${t}}}`}</button>
                ))}
              </div>
            </div>

            <DialogFooter className="pt-2">
              <Button variant="outline" onClick={onClose} className="rounded-xl">Batal</Button>
              <Button onClick={save} disabled={saving} data-testid="tpl-save" className="bg-[#064E3B] hover:bg-[#043E2F] rounded-xl">
                {saving && <Loader2 className="animate-spin mr-2" size={16} />} Simpan Template
              </Button>
            </DialogFooter>
          </div>

          {/* Kolom kanan: pratinjau langsung */}
          <div className="hidden lg:block bg-slate-100 border-l border-slate-200 overflow-auto custom-scroll p-4" data-testid="tpl-preview-panel">
            <div className="text-xs font-medium text-slate-500 mb-2 flex items-center gap-1"><Eye size={13} /> Pratinjau</div>
            {body.trim() ? (
              <div className="bg-white shadow-sm rounded-lg overflow-hidden">
                <FitScale width={794}>
                  <LetterDocument letter={previewLetter} profile={profile} terms={terms} paper="A4" showKopAddress showKop={hasKop} showNumber={hasNumber} lineSpacing={1.5} fontPt={11} />
                </FitScale>
              </div>
            ) : (
              <div className="h-full grid place-items-center text-sm text-slate-400 py-16">Isi surat untuk melihat pratinjau…</div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Dokumen surat (pratinjau & cetak) ---------------- */
// Ubah teks UPPERCASE (hasil scan KK) jadi Title Case rapi; pertahankan akronim pendek (WNI, PNS, RT).
function toTitleId(s) {
  if (s == null) return "";
  return String(s).replace(/[A-Za-zÀ-ÿ]+/g, (w) => {
    if (/^[A-Z]{2,3}$/.test(w)) return w; // akronim pendek dibiarkan (WNI, PNS, TNI, RT, RW)
    return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
  });
}

// Grid identitas: titik dua (:) sejajar; item utama sejajar dgn alinea (1,25cm), sub-item lebih menjorok.
function renderIdentGrid(rows, key) {
  const gridCols = { gridTemplateColumns: "max-content max-content 1fr" };
  return (
    <div key={`i${key}`} className="mb-3" style={{ paddingLeft: "1.25cm" }}>
      <div className="grid gap-x-2 gap-y-0.5" style={gridCols}>
        {rows.flatMap((r, idx) => {
          const isName = /^nama( lengkap)?$/i.test(String(r.label || "").trim());
          return [
            <div key={`l${idx}`} style={r.sub ? { paddingLeft: "4rem" } : undefined}>{r.label}</div>,
            <div key={`c${idx}`}>{r.label ? ":" : ""}</div>,
            <div key={`v${idx}`} className={isName ? "font-bold" : ""} style={{ whiteSpace: "pre-line" }}>{isName ? r.value : toTitleId(r.value)}</div>,
          ];
        })}
      </div>
    </div>
  );
}

// Label identitas umum -> baris "Label : nilai" tunggal pun dirender sebagai grid
// agar nilai panjang yang membungkus SELALU sejajar dengan baris pertama nilai (hanging indent).
const IDENT_LABEL_RE = /^(nama( lengkap)?|nik|nip|no\.?( kk| ktp| hp| telp\.?| telepon)?|nomor|tempat(\/| )?(dan )?tanggal lahir|tempat lahir|tanggal lahir|ttl|jenis kelamin|kelamin|agama|pekerjaan|alamat|kewarganegaraan|kebangsaan|warga negara|status( perkawinan)?|pendidikan|golongan darah|gol\.? darah|umur|jabatan|bin|binti|tempat tinggal|rt|rw)\b/i;

// Deteksi baris identitas bebas-format (AI): "Label : nilai" atau "Label <tab> nilai".
function parseIdentityLine(line) {
  if (!line || !line.trim()) return null;
  const mm = line.match(/^\s*([^:\t]{1,32}?)\s*(?::|\t)\s*(\S.*)$/);
  if (!mm) return null;
  const label = mm[1].trim();
  const value = mm[2].trim();
  if (!label || /[.]$/.test(label)) return null;      // bukan label kalau diakhiri titik
  if (label.split(/\s+/).length > 5) return null;      // label terlalu panjang -> kemungkinan kalimat
  const sub = label.startsWith("» ");
  return { label: sub ? label.slice(2).trim() : label, value, sub };
}

// Paragraf isi: alinea (indent baris pertama 1,25cm = standar surat resmi) untuk SETIAP paragraf
// yang lebih dari 1 baris (termasuk kalimat pembuka). Baris tunggal (mis. pengantar) rapat ke kiri.
function Paragraph({ text, forceIndent = false }) {
  const ref = useRef(null);
  const [indent, setIndent] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => {
      const cs = window.getComputedStyle(el);
      const lh = parseFloat(cs.lineHeight) || parseFloat(cs.fontSize) * 1.4 || 16;
      const lines = Math.round(el.offsetHeight / lh);
      setIndent((forceIndent || lines > 1) ? "1.25cm" : 0);
    };
    measure();
    let ro;
    if (typeof ResizeObserver !== "undefined") { ro = new ResizeObserver(measure); ro.observe(el); }
    return () => { if (ro) ro.disconnect(); };
  }, [text, forceIndent]);
  return (
    <p ref={ref} className="whitespace-pre-wrap mb-3" style={{ textAlign: "justify", textIndent: indent }}>{text}</p>
  );
}

// Deteksi baris tabel: memuat '|' & minimal 2 kolom (mis. "No | Nama | Ket").
function isTableRow(l) {
  const s = (l || "").trim();
  if (!s || !s.includes("|")) return false;
  return s.replace(/^\||\|$/g, "").split("|").length >= 2;
}

// Render tabel dari kumpulan baris "a | b | c". Baris pertama = header.
// Baris pemisah markdown (---|---) diabaikan. Border ikut tercetak.
function renderTable(linesArr, key) {
  const rows = (linesArr || [])
    .map((l) => (l || "").trim())
    .filter(Boolean)
    .map((l) => l.replace(/^\||\|$/g, "").split("|").map((c) => c.trim()))
    .filter((cells) => !cells.every((c) => c === "" || /^:?-{2,}:?$/.test(c)));
  if (!rows.length) return null;
  const cols = Math.max(...rows.map((r) => r.length));
  const norm = rows.map((r) => { const a = [...r]; while (a.length < cols) a.push(""); return a; });
  const header = norm[0];
  const bodyRows = norm.slice(1);
  const bd = "1px solid #111827";
  return (
    <table key={`tb${key}`} className="my-3 text-sm" style={{ borderCollapse: "collapse", width: "100%" }} data-testid="letter-table-block">
      <thead>
        <tr>{header.map((h, ci) => <th key={ci} style={{ border: bd, padding: "3px 6px", textAlign: "center", fontWeight: 700 }}>{h}</th>)}</tr>
      </thead>
      <tbody>
        {bodyRows.map((r, ri) => (
          <tr key={ri}>{r.map((c, ci) => <td key={ci} style={{ border: bd, padding: "3px 6px", verticalAlign: "top", whiteSpace: "pre-line" }}>{c}</td>)}</tr>
        ))}
      </tbody>
    </table>
  );
}

// Baris daftar bernomor "1. ..." / "1) ..." atau bullet "- ..." / "• ..."
const LIST_RE = /^\s*(\d+[.)]|[-•*])\s+(.*)$/;
function parseListLine(line) {
  const m = (line || "").match(LIST_RE);
  if (!m) return null;
  return { marker: /^\d/.test(m[1]) ? m[1] : "•", text: m[2] };
}
// Render daftar: kolom penanda + kolom teks -> baris membungkus rata di bawah teks (hanging indent).
// PENOMORAN OTOMATIS: bila daftar bertipe angka, nomor di-generate berurutan (1,2,3,...) tanpa
// bergantung angka yang diketik pengguna — menambah/menghapus/menyisipkan poin otomatis renumber.
function renderList(items, key) {
  const ordered = /\d/.test(items[0]?.marker || "");
  return (
    <div key={`ol${key}`} className="mb-3" style={{ paddingLeft: "1.25cm" }} data-testid="letter-list-block">
      {items.map((it, idx) => (
        <div key={idx} className="grid gap-x-2 gap-y-0.5" style={{ gridTemplateColumns: "max-content 1fr" }}>
          <div style={{ textAlign: "right" }}>{ordered ? `${idx + 1}.` : "•"}</div>
          <div style={{ textAlign: "justify", whiteSpace: "pre-line" }}>{it.text}</div>
        </div>
      ))}
    </div>
  );
}

// Render isi surat: blok [[IDENT]] & grup identitas plain-text -> grid rapi; sisanya paragraf justify.
// Penanda [[PAGEBREAK]] memisahkan halaman (ganti halaman saat cetak + garis pemisah di layar),
// agar surat hasil tetap berhalaman sama seperti file aslinya.
function renderLetterBody(body) {
  const segments = [];
  let k = 0;
  const re = /\[\[(IDENT|TABLE)\]\]\n([\s\S]*?)\n\[\[\/\1\]\]/g;

  const pushParagraphs = (raw) => {
    const block = (raw || "").replace(/^\n+|\n+$/g, "");
    if (!block) return;
    block.split(/\n[ \t]*\n/).forEach((para) => {
      const forceIndent = /^[ \t]+\S/.test(para); // diawali tab/spasi -> alinea manual
      const txt = para.replace(/^[ \t]+/, "").replace(/\n[ \t]+/g, "\n");
      if (txt.trim()) segments.push(<Paragraph key={`t${k++}`} text={txt} forceIndent={forceIndent} />);
    });
  };

  // Untuk teks di luar [[IDENT]]: cari GRUP baris identitas lalu ratakan seperti grid.
  // Baris pendek non-identitas yang menempel (tanpa baris kosong) DI BAWAH baris identitas
  // dianggap LANJUTAN nilai baris identitas sebelumnya (mis. Jabatan yang panjang jadi 2 baris).
  const pushText = (raw) => {
    const lines = (raw || "").split("\n");
    let i = 0;
    let buf = [];
    const flush = () => { if (buf.length) { pushParagraphs(buf.join("\n")); buf = []; } };
    while (i < lines.length) {
      // Deteksi TABEL (baris ber-'|') lebih dulu -> render sebagai tabel.
      if (isTableRow(lines[i])) {
        let j = i;
        const trows = [];
        while (j < lines.length && isTableRow(lines[j])) { trows.push(lines[j]); j++; }
        const tb = renderTable(trows, k++);
        if (tb) { flush(); segments.push(tb); i = j; continue; }
      }
      // Deteksi DAFTAR bernomor/bullet -> render sebagai daftar rapi.
      if (parseListLine(lines[i])) {
        let j = i;
        const items = [];
        while (j < lines.length) {
          const it = parseListLine(lines[j]);
          if (it) { items.push(it); j++; }
          else if (items.length && lines[j].trim() && /^\s+\S/.test(lines[j])) {
            items[items.length - 1].text += "\n" + lines[j].trim(); j++; // lanjutan item
          } else break;
        }
        if (items.length) { flush(); segments.push(renderList(items, k++)); i = j; continue; }
      }
      if (parseIdentityLine(lines[i])) {
        let j = i;
        const rows = [];
        while (j < lines.length && lines[j].trim() !== "") {
          const row = parseIdentityLine(lines[j]);
          if (row) { rows.push(row); }
          else if (rows.length) {
            const cont = lines[j].trim();
            const isLeadIn = /[:：]$/.test(cont);           // baris pengantar -> bukan lanjutan
            if (!isLeadIn && cont.length <= 45) {
              rows[rows.length - 1].value = (rows[rows.length - 1].value + "\n" + cont).trim();
            } else break;                                    // kalimat baru -> hentikan grup
          } else break;
          j++;
        }
        if (rows.length >= 2 || (rows.length === 1 && IDENT_LABEL_RE.test(rows[0].label.trim()))) {
          flush(); segments.push(renderIdentGrid(rows, k++)); i = j; continue;
        }
      }
      buf.push(lines[i]); i++;
    }
    flush();
  };

  // Proses satu "halaman" (potongan body di antara penanda [[PAGEBREAK]]).
  const processPage = (pageBody) => {
    let last = 0, m;
    re.lastIndex = 0;
    while ((m = re.exec(pageBody)) !== null) {
      if (m.index > last) pushText(pageBody.slice(last, m.index));
      if (m[1] === "TABLE") {
        const tb = renderTable(m[2].split("\n"), k++);
        if (tb) segments.push(tb);
      } else {
        const rows = m[2].split("\n").map((line) => {
          const idx = line.indexOf("\t");
          const label = idx === -1 ? line : line.slice(0, idx);
          const value = idx === -1 ? "" : line.slice(idx + 1);
          const sub = label.startsWith("» ");
          return { label: sub ? label.slice(2) : label, value, sub };
        });
        segments.push(renderIdentGrid(rows, k++));
      }
      last = re.lastIndex;
    }
    if (last < pageBody.length) pushText(pageBody.slice(last));
  };

  // Pisahkan berdasarkan penanda ganti halaman lalu render tiap halaman.
  const pages = String(body || "").split(/\n*\[\[PAGEBREAK\]\]\n*/);
  pages.forEach((pageBody, pi) => {
    if (pi > 0) segments.push(<div key={`pb${k++}`} className="letter-pagebreak" aria-hidden="true" />);
    processPage(pageBody);
  });
  return segments;
}

// Teks kop yang otomatis mengecil agar SELALU muat 1 baris (ukuran sebesar mungkin).
function FitText({ text, max = 28, min = 11, className = "", weight = 700 }) {
  const ref = useRef(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const fit = () => {
      const parent = el.parentElement;
      if (!parent) return;
      const avail = parent.clientWidth;
      if (!avail) return;
      let fs = max;
      el.style.fontSize = fs + "px";
      let guard = 0;
      while (el.scrollWidth > avail && fs > min && guard < 400) { fs -= 0.5; el.style.fontSize = fs + "px"; guard++; }
    };
    fit();
    let ro;
    if (typeof ResizeObserver !== "undefined" && el.parentElement) {
      ro = new ResizeObserver(fit);
      ro.observe(el.parentElement);
    }
    return () => { if (ro) ro.disconnect(); };
  }, [text, max, min]);
  return <div ref={ref} className={className} style={{ whiteSpace: "nowrap", fontSize: max, lineHeight: 1.15, fontWeight: weight }}>{text}</div>;
}

// Skala dokumen agar muat lebar panel pratinjau (tanpa scroll horizontal).
function FitScale({ width, children }) {
  const outer = useRef(null), inner = useRef(null);
  const [scale, setScale] = useState(1);
  const [h, setH] = useState(0);
  useLayoutEffect(() => {
    const measure = () => {
      const avail = outer.current?.clientWidth || width;
      const s = Math.min(1, avail / width);
      setScale(s);
      if (inner.current) setH(inner.current.offsetHeight * s);
    };
    measure();
    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(measure);
      if (outer.current) ro.observe(outer.current);
      if (inner.current) ro.observe(inner.current);
    }
    return () => { if (ro) ro.disconnect(); };
  }, [width]);
  return (
    <div ref={outer} style={{ position: "relative", width: "100%", height: h || undefined }}>
      <div ref={inner} style={{ position: "absolute", top: 0, left: 0, width, transform: `scale(${scale})`, transformOrigin: "top left" }}>
        {children}
      </div>
    </div>
  );
}

function LetterDocument({ letter, profile, terms, paper = "A4", showKopAddress = true, showKop = true, showNumber = true, lineSpacing = 1.5, fontPt = 11 }) {
  const t = terms || {};
  const villageTerm = t.village_term || "Desa";
  const headTerm = t.head_term || "Kepala Desa";
  const districtTerm = t.district_term || "Kecamatan";
  const vn = profile?.village_name || "";
  // Jabatan kepala: bila memakai istilah lain (mis. Keuchik), sebutkan penyebutan desanya.
  const jabatanKepala = villageTerm && !headTerm.toLowerCase().includes(villageTerm.toLowerCase())
    ? `${headTerm} ${villageTerm} ${vn}`.trim()
    : `${headTerm} ${vn}`.trim();
  const today = new Date().toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" });

  // Kop kabupaten: "Pemerintah {sebutan kabupaten} {nama}" (mis. Pemerintah Kabupaten Aceh Utara)
  const regRaw = (profile?.regency_name || "").trim();
  const lowReg = regRaw.toLowerCase();
  let sebutanKab = t.regency_term || "Kabupaten";
  let namaKab = regRaw;
  if (lowReg.startsWith("kota ")) { sebutanKab = "Kota"; namaKab = regRaw.slice(5).trim(); }
  else if (lowReg.startsWith("kabupaten ")) { sebutanKab = "Kabupaten"; namaKab = regRaw.slice(10).trim(); }
  const kopKabupaten = `Pemerintah ${sebutanKab} ${namaKab}`.trim();

  const body = letter.body_rendered || "";
  const width = paper === "F4" ? "215mm" : "210mm";
  // Margin isi surat: 2,7cm kiri/kanan/bawah (atas mengikuti kop, tanpa margin halaman)
  const bodyPad = { paddingLeft: "27mm", paddingRight: "27mm", paddingBottom: "27mm", paddingTop: showKop ? "6mm" : "27mm" };

  return (
    <div className="print-area letter-doc bg-white text-slate-900 mx-auto" style={{ fontFamily: "'Bookman Old Style','Book Antiqua','Palatino Linotype',Palatino,Georgia,serif", fontSize: `${fontPt}pt`, width, maxWidth: "100%" }}>
      {/* Kop surat — FULL WIDTH, tanpa margin kiri/kanan/atas. Garis: tebal di atas, tipis di bawah. */}
      {showKop && (
      <div>
        <div className="flex items-center gap-4 pt-3 pb-2 px-4">
          {profile?.regency_logo_url ? (
            <img src={profile.regency_logo_url} alt="logo" className="object-contain shrink-0" style={{ height: "112px", width: "112px", marginLeft: "10px" }} />
          ) : (
            <div className="rounded bg-slate-100 shrink-0" style={{ height: "112px", width: "112px", marginLeft: "10px" }} />
          )}
          <div className="flex-1 min-w-0 text-center leading-tight">
            <FitText text={kopKabupaten} max={24} className="uppercase" weight={700} />
            <FitText text={`${districtTerm} ${profile?.district_name || ""}`.trim()} max={24} className="uppercase" weight={700} />
            <FitText text={`${villageTerm} ${profile?.village_name || ""}`.trim()} max={34} className="uppercase" weight={800} />
            {showKopAddress && (
              <>
                <div className="text-xs mt-1">{profile?.address || "Alamat Kantor"}{profile?.postal_code ? ` — Kode Pos ${profile.postal_code}` : ""}</div>
                {(profile?.phone || profile?.email) && (
                  <div className="text-xs">{[profile?.phone, profile?.email].filter(Boolean).join(" • ")}</div>
                )}
              </>
            )}
          </div>
        </div>
        <div style={{ borderTop: "3px solid #111827", marginLeft: "0.4cm", marginRight: "0.4cm" }} />
        <div style={{ borderTop: "1px solid #111827", marginTop: "2px", marginLeft: "0.4cm", marginRight: "0.4cm" }} />
      </div>
      )}

      {/* Isi surat — dengan margin 2,7cm & spasi baris yang bisa diatur */}
      <div style={{ ...bodyPad, lineHeight: lineSpacing }}>
        <div className="text-center mb-5">
          <div className="text-lg font-bold uppercase underline">{letter.letter_type_name}</div>
          {showNumber && <div className="text-sm">Nomor: {letter.letter_number}</div>}
        </div>

        <div data-testid="letter-body">{renderLetterBody(body)}</div>

        {Array.isArray(letter.signers) && letter.signers.length > 0 ? (
          <div className="mt-8 space-y-8" data-testid="letter-signers">
            {letter.signers.map((row, ri) => (
              <div key={ri}>
                {row.heading ? <div className="text-center text-sm mb-1 whitespace-pre-line">{row.heading}</div> : null}
                <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${Math.max(1, (row.cells || []).length)}, minmax(0, 1fr))` }}>
                  {(row.cells || []).map((c, ci) => (
                    <div key={ci} className="text-center text-sm">
                      {c.show_place_date ? <div className="whitespace-pre-line">{c.place_date_text || `${vn}, ${today}`}</div> : null}
                      {c.label ? <div className="whitespace-pre-line">{c.label}</div> : null}
                      {c.jabatan ? <div className="whitespace-pre-line">{c.jabatan}</div> : null}
                      <div className="h-20" />
                      <div className="font-bold underline uppercase">{c.name || "( ................................. )"}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex justify-end mt-8">
            <div className="text-center text-sm w-64">
              <div>{profile?.village_name}, {today}</div>
              <div>{jabatanKepala}</div>
              <div className="h-20" />
              <div className="font-bold underline uppercase">{profile?.village_head_name || "( ................................. )"}</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
